import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost,
  Modal, Field, Input, Textarea, Empty, StatusBadge, Select,
} from "@/components/primitives";
import LineItemsEditor from "@/components/LineItemsEditor";
import { toast } from "sonner";
import { Plus, DownloadSimple, Eye } from "@phosphor-icons/react";
import SmartInput from "@/components/SmartInput";

export default function Orders() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [skus, setSkus] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(null);
  const [form, setForm] = useState({ customer: "", notes: "" });
  const [items, setItems] = useState([]);
  const [tax, setTax] = useState(16);
  const canEdit = can(user.role, "staff");

  async function load() {
    try { const { data } = await api.get("/orders"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const [inv, cust] = await Promise.all([
          api.get("/inventory"),
          api.get("/customers?active_only=true"),
        ]);
        setSkus(inv.data); setCustomers(cust.data);
      } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
    })();
  }, []);

  function openCreate() {
    setForm({ customer: "", notes: "" });
    setItems([{ description: "", sku: "", quantity: 1, unit_price: 0 }]);
    setTax(16); setCreateOpen(true);
  }
  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/orders", {
        customer: form.customer, notes: form.notes,
        tax_rate: Number(tax),
        items: items.map((i) => ({
          description: i.description, sku: i.sku || null,
          quantity: Number(i.quantity), unit_price: Number(i.unit_price),
        })),
      });
      toast.success("Order created");
      setCreateOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function setStatus(o, status) {
    try {
      await api.patch(`/orders/${o.id}/status?status=${status}`);
      toast.success("Updated");
      setViewOpen(null); load();
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="orders-page">
      <PageHeader
        overline="Module / Sales"
        title="Orders"
        subtitle="Sales orders generated directly or converted from quotations."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `orders-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canEdit && (
              <BtnPrimary onClick={openCreate} data-testid="order-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> New Order
              </BtnPrimary>
            )}
          </>
        }
      />

      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No orders yet." /></div> : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr><th>Number</th><th>Customer</th><th>Created</th>
                  <th className="text-right">Total</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} data-testid={`order-row-${o.number}`}>
                    <td className="mono font-semibold">{o.number}</td>
                    <td>{o.customer}</td>
                    <td className="text-sm text-[#52525b]">{fmtDate(o.created_at)}</td>
                    <td className="text-right mono font-semibold">{fmtZMW(o.total)}</td>
                    <td><StatusBadge status={o.status} /></td>
                    <td className="text-right">
                      <BtnGhost onClick={() => setViewOpen(o)}>
                        <Eye size={12} weight="bold" /> View
                      </BtnGhost>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)}
        title="New Order" wide testId="order-create-modal">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Customer" hint="Pick from saved customers or type a new one">
            <SmartInput
              required value={form.customer}
              testId="o-customer"
              onChange={(v) => setForm({ ...form, customer: v })}
              options={customers.map((c) => ({ value: c.name, hint: c.code }))}
            />
          </Field>
          <LineItemsEditor items={items} setItems={setItems} taxRate={tax} setTaxRate={setTax} skuOptions={skus} />
          <Field label="Notes">
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setCreateOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="o-submit">Create Order</BtnPrimary>
          </div>
        </form>
      </Modal>

      <Modal open={!!viewOpen} onClose={() => setViewOpen(null)}
        title={viewOpen ? `Order ${viewOpen.number}` : ""} wide>
        {viewOpen && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="phizo-label">Customer</div><div className="font-semibold">{viewOpen.customer}</div></div>
              <div><div className="phizo-label">Total</div><div className="font-heading text-xl font-black">{fmtZMW(viewOpen.total)}</div></div>
            </div>
            <div className="border border-[#e4e4e7]">
              <table className="w-full text-sm">
                <thead className="bg-[#f4f4f5]"><tr>
                  <th className="text-left p-2 phizo-label">Item</th>
                  <th className="text-right p-2 phizo-label">Qty</th>
                  <th className="text-right p-2 phizo-label">Price</th>
                  <th className="text-right p-2 phizo-label">Line Total</th>
                </tr></thead>
                <tbody>
                  {viewOpen.items.map((it, idx) => (
                    <tr key={idx} className="border-t border-[#e4e4e7]">
                      <td className="p-2">{it.description}</td>
                      <td className="p-2 text-right mono">{it.quantity}</td>
                      <td className="p-2 text-right mono">{fmtZMW(it.unit_price)}</td>
                      <td className="p-2 text-right mono">{fmtZMW(it.quantity * it.unit_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {canEdit && (
              <div className="flex flex-wrap gap-2 pt-3 border-t border-[#e4e4e7]">
                {["pending", "processing", "fulfilled", "cancelled"].map((s) => (
                  <BtnSecondary key={s} onClick={() => setStatus(viewOpen, s)}>{s}</BtnSecondary>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
